import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaCheckCircle, FaTimesCircle, FaBan, FaClock,
  FaShieldAlt, FaBuilding, FaIdCard, FaCalendarAlt, FaUser
} from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import './BadgeVerify.css';

const STATUS_UI = {
  active: { icon: FaCheckCircle, title: 'Verified', subtitle: 'This badge is active and valid', className: 'bv-valid' },
  revoked: { icon: FaBan, title: 'Revoked', subtitle: 'This badge has been revoked', className: 'bv-revoked' },
  expired: { icon: FaTimesCircle, title: 'Expired', subtitle: 'This badge has expired', className: 'bv-expired' },
  suspended: { icon: FaClock, title: 'Suspended', subtitle: 'This badge is temporarily suspended', className: 'bv-suspended' },
};

export default function BadgeVerify() {
  const { token } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(getApiEndpoint(`/api/badges/verify/${token}`));
        if (!res.ok) throw new Error('Verification failed');
        setResult(await res.json());
      } catch {
        setError('Could not verify this badge. It may be invalid or the service is unavailable.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="bv-page">
        <div className="bv-container">
          <div className="bv-loading">
            <div className="bv-spinner" />
            <p>Verifying badge…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bv-page">
        <div className="bv-container">
          <div className="bv-brand">
            <img src={cloudsafeLogo} alt="CloudSafe" className="bv-logo" />
            <span>CloudSafe</span>
          </div>
          <div className="bv-result bv-error">
            <FaTimesCircle className="bv-result-icon" />
            <h2>Verification Failed</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const status = result.valid ? 'active' : (result.reason?.includes('revoked') ? 'revoked' : result.reason?.includes('expired') ? 'expired' : 'suspended');
  const ui = STATUS_UI[status] || STATUS_UI.suspended;
  const Icon = ui.icon;

  return (
    <div className="bv-page">
      <div className="bv-container">
        <div className="bv-brand">
          <img src={cloudsafeLogo} alt="CloudSafe" className="bv-logo" />
          <span>CloudSafe</span>
        </div>

        <div className={`bv-result ${ui.className}`}>
          <Icon className="bv-result-icon" />
          <h2>{ui.title}</h2>
          <p>{result.valid ? ui.subtitle : result.reason}</p>
        </div>

        {(result.displayName || result.badgeNumber) && (
          <div className="bv-details">
            {result.displayName && (
              <div className="bv-detail-row">
                <FaUser className="bv-detail-icon" />
                <div>
                  <span className="bv-detail-label">Name</span>
                  <span className="bv-detail-value">{result.displayName}</span>
                </div>
              </div>
            )}
            {result.role && (
              <div className="bv-detail-row">
                <FaIdCard className="bv-detail-icon" />
                <div>
                  <span className="bv-detail-label">Role</span>
                  <span className="bv-detail-value">{result.role}</span>
                </div>
              </div>
            )}
            {result.companyName && (
              <div className="bv-detail-row">
                <FaBuilding className="bv-detail-icon" />
                <div>
                  <span className="bv-detail-label">Company</span>
                  <span className="bv-detail-value">{result.companyName}</span>
                </div>
              </div>
            )}
            {result.badgeNumber && (
              <div className="bv-detail-row">
                <FaShieldAlt className="bv-detail-icon" />
                <div>
                  <span className="bv-detail-label">Badge ID</span>
                  <span className="bv-detail-value">{result.badgeNumber}</span>
                </div>
              </div>
            )}
            {result.issuedAt && (
              <div className="bv-detail-row">
                <FaCalendarAlt className="bv-detail-icon" />
                <div>
                  <span className="bv-detail-label">Issued</span>
                  <span className="bv-detail-value">
                    {new Date(result.issuedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bv-footer">
          <FaShieldAlt />
          <span>Verified by CloudSafe — cloud-safe.uk</span>
        </div>
      </div>
    </div>
  );
}
